import { LoadingSpinner } from "components/LoadingSpinner";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useParams } from "react-router-dom";
import { useGetPublicCredentialQuery } from "store/queries/training";
import styled from "styled-components";
import {
  Certificate,
  Certifies,
  DateLine,
  Eyebrow,
  formatCertificateDate,
  Hospital,
  Name,
  Ribbon,
  Title,
  Version,
} from "training/components/CertificateParts";

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 24px 16px;
`;

const Status = styled.div<{ $current: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 20px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: var(--font-weight-semibold, 600);
  color: ${({ $current }) =>
    $current ? "var(--green-700, #15803d)" : "var(--brand-gray-600)"};
  background: ${({ $current }) =>
    $current ? "var(--green-50, #f0fdf4)" : "var(--surface-100, #f3f4f6)"};
`;

const Note = styled.p`
  margin: 12px auto 0;
  max-width: 460px;
  color: var(--brand-gray-600);
`;

const NotFoundTitle = styled.h1`
  margin: 0 0 12px;
  font-size: 1.5rem;
`;

/**
 * CLE-3015 — /certified/:credentialId, the public (no login) verification page a Clear Certified
 * user shares, and the certificate URL on their LinkedIn profile. Three states: a current
 * credential, a credential that is no longer current (its holder's training was reset, or their
 * access was removed — the note says neither, so the page does not disclose which), and an
 * unknown id. It shows only what the public endpoint returns: name, hospital, date, version, status.
 *
 * A link preview (Open Graph tags) needs server-rendered HTML, which this page cannot give; see
 * "Sharing a certificate" in brain/wiki/pages/features/CLEAR-CERTIFIED-TRAINING.md.
 */
export function PublicCertifiedPage() {
  const { credentialId = "" } = useParams();
  const { data, isLoading, error } = useGetPublicCredentialQuery(credentialId, {
    skip: !credentialId,
  });
  const credential = data?.data;
  const notFound =
    !credentialId ||
    (!!error && "status" in error && error.status === 404) ||
    (!isLoading && !error && !credential);

  let title = "Clear Certified";
  if (credential) title = `${credential.name} · Clear Certified`;
  else if (notFound) title = "Credential not found · Clear Certified";
  useDocumentTitle(title);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (notFound || !credential) {
    return (
      <Wrapper>
        <Certificate aria-label="Credential not found">
          <NotFoundTitle>
            {notFound
              ? "We could not find this credential"
              : "This credential could not be checked right now"}
          </NotFoundTitle>
          <Note>
            {notFound
              ? "Check that the link was copied in full. ClearHealth credential links end in a 12-character code."
              : "Please reload the page in a moment."}
          </Note>
        </Certificate>
      </Wrapper>
    );
  }

  const current = credential.status === "current";

  return (
    <Wrapper>
      <Certificate aria-label="Clear Certified credential">
        <Ribbon data-ribbon>
          <i className="pi pi-verified" aria-hidden />
        </Ribbon>
        <Eyebrow>Verified credential</Eyebrow>
        <Title>Clear Certified</Title>
        <Certifies>Issued by ClearHealth to</Certifies>
        <Name data-testid="credential-name">{credential.name}</Name>
        {credential.hospitalName ? (
          <Hospital data-testid="credential-hospital">
            {credential.hospitalName}
          </Hospital>
        ) : null}
        {current && credential.certifiedAt ? (
          <DateLine data-testid="credential-date">
            on {formatCertificateDate(credential.certifiedAt)}
          </DateLine>
        ) : null}
        <Status $current={current} data-testid="credential-status">
          <i
            className={current ? "pi pi-check-circle" : "pi pi-info-circle"}
            aria-hidden
          />
          {current ? "Current" : "Not current"}
        </Status>
        {current ? null : (
          <Note>This person is not currently Clear Certified.</Note>
        )}
        {current && credential.curriculumVersion ? (
          <Version>Curriculum version {credential.curriculumVersion}</Version>
        ) : null}
      </Certificate>
    </Wrapper>
  );
}
